import { Card, Heading, Text } from '@ui/themed';
import { t } from 'i18next';
import { DEFAULT_CURRENCY } from '../../../constants/currency';
import { useSettings } from '../../../context/settings-context';
import { formatMoney } from '../../../utils/format-money';
import type { PaymentOnAccount } from '../types/customer';
import { OptionalDetail } from './optional-detail';
import { TwoColumnLayout } from './two-column-layout';

function optionalMoney(currencyCode: string, value?: number | null) {
  return value != null ? formatMoney(value, currencyCode) : undefined;
}

function useDisplayCurrency(poc?: PaymentOnAccount): string {
  const settings = useSettings();
  const fallback = settings.baseCurrency === '' ? DEFAULT_CURRENCY : settings.baseCurrency;
  return poc?.currency_code ?? fallback;
}

export function CustomerFinancialsCard({
  paymentOnAccount,
  discountPercentage,
  accountingId,
}: {
  paymentOnAccount?: PaymentOnAccount;
  discountPercentage?: number;
  accountingId?: string;
}) {
  const displayCurrency = useDisplayCurrency(paymentOnAccount);

  return (
    <Card>
      <Card.Header headingLevel="h2">
        {t('features:customers.components.customer_financials_card.header')}
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-2">
          <TwoColumnLayout>
            <TwoColumnLayout.Column>
              <Text>
                {t('features:customers.components.customer_financials_card.discount_percentage')}
              </Text>
              <Text>
                {t('features:customers.components.customer_financials_card.accounting_id')}
              </Text>
              <Text></Text>
              <Heading level="h3" style="sm">
                {t(
                  'features:customers.components.customer_financials_card.payment_on_account.label',
                )}
              </Heading>
            </TwoColumnLayout.Column>
            <TwoColumnLayout.Column>
              <Text>
                {discountPercentage ? `${discountPercentage}%` : t('labels:none_selected')}
              </Text>
              <OptionalDetail value={accountingId} />
            </TwoColumnLayout.Column>
          </TwoColumnLayout>
          <TwoColumnLayout>
            <TwoColumnLayout.Column>
              <Text>
                {t(
                  'features:customers.components.customer_financials_card.payment_on_account.balance',
                )}
              </Text>
              <Text>
                {t(
                  'features:customers.components.customer_financials_card.payment_on_account.credit_limit',
                )}
              </Text>
              <Text>
                {t(
                  'features:customers.components.customer_financials_card.payment_on_account.net_terms',
                )}
              </Text>
              <Text>
                {t(
                  'features:customers.components.customer_financials_card.payment_on_account.currency',
                )}
              </Text>
            </TwoColumnLayout.Column>
            <TwoColumnLayout.Column>
              <OptionalDetail value={optionalMoney(displayCurrency, paymentOnAccount?.balance)} />
              <OptionalDetail
                value={optionalMoney(displayCurrency, paymentOnAccount?.credit_limit)}
              />
              <OptionalDetail value={paymentOnAccount?.net_terms?.split('_').join(' ')} />
              <OptionalDetail value={paymentOnAccount?.currency_code} />
            </TwoColumnLayout.Column>
          </TwoColumnLayout>
        </div>
      </Card.Body>
    </Card>
  );
}
